import { BYOKProvider } from '@developerz/domain';
import { createUniqueId, type JSX } from 'solid-js';
import { t } from '../i18n';
import type { FieldA11y } from './Field';
import { providerLabel } from './KeyCard';
import { Select, type SelectOption } from './Select';

// Grouped provider picklist (arc 101 slice 03b). Provider is OPTIONAL on a route
// (empty = "any provider the pool resolves"), so the first option is blank. The
// groups come from the domain enum (never hardcoded strings): "Popular" =
// first-class hosts, "Other" = the OpenAI-wire third parties, "Custom" = a
// bring-your-own endpoint. Reused wherever a route needs a provider pin.

type ProviderGroup = { readonly labelKey: string; readonly providers: readonly BYOKProvider[] };

/**
 * The CUSTOMER-facing provider picklist (#4954). `BYOKProvider.Router` is the
 * platform's internal gateway (#4889) — the owner ruled it server-only on
 * 2026-09-24 because a customer who picks it gets the internal default host
 * from `PROVIDER_WIRE` with no way to override it. Server-only surfaces that
 * need to dial the gateway (the worker, the agent runner, internal admin tools)
 * read `BYOKProvider.Router` directly, never through this picklist. So the
 * union of `PROVIDER_GROUPS` is EVERY CUSTOMER-ADDRESSABLE enum member, and
 * `ProviderSelect.test` is updated to assert that — Router is server-only, not a
 * missing placement.
 */
export const CUSTOMER_ADDRESSABLE_PROVIDERS: ReadonlySet<BYOKProvider> = new Set([
  BYOKProvider.Anthropic,
  BYOKProvider.OpenAI,
  BYOKProvider.Gemini,
  BYOKProvider.OpenRouter,
  BYOKProvider.Kimi,
  BYOKProvider.Zai,
  BYOKProvider.Cerebras,
  BYOKProvider.Minimax,
  BYOKProvider.Groq,
  BYOKProvider.Xai,
  BYOKProvider.Deepseek,
  BYOKProvider.Together,
  BYOKProvider.Fireworks,
  BYOKProvider.Alibaba,
  BYOKProvider.Custom,
]);

export const PROVIDER_GROUPS: readonly ProviderGroup[] = [
  {
    labelKey: 'account_keys.routes.provider_group_popular',
    providers: [
      BYOKProvider.Anthropic,
      BYOKProvider.OpenAI,
      BYOKProvider.Gemini,
      BYOKProvider.OpenRouter,
      BYOKProvider.Kimi,
      BYOKProvider.Zai,
    ],
  },
  {
    labelKey: 'account_keys.routes.provider_group_other',
    providers: [
      BYOKProvider.Cerebras,
      BYOKProvider.Minimax,
      BYOKProvider.Groq,
      BYOKProvider.Xai,
      BYOKProvider.Deepseek,
      BYOKProvider.Together,
      BYOKProvider.Fireworks,
      // Arc 101 slice 02: an OpenAI-wire third party like the rest of this group;
      // its endpoint is REQUIRED (workspace-scoped), which the capture form states
      // on the field — the group says nothing about endpoints.
      BYOKProvider.Alibaba,
    ],
  },
  {
    labelKey: 'account_keys.routes.provider_group_custom',
    providers: [BYOKProvider.Custom],
  },
];

/** `PROVIDER_GROUPS` as `Select` options, group-labelled; the blank "any
 *  provider" choice — when offered — leads, ungrouped, same position the old
 *  native select's first option held. */
function providerOptions(allowAny: boolean): readonly SelectOption<string>[] {
  const groups = PROVIDER_GROUPS.flatMap((group) =>
    group.providers.map((p) => ({
      value: p as string,
      label: providerLabel(p),
      group: t(group.labelKey),
    })),
  );
  return allowAny
    ? [{ value: '', label: t('account_keys.routes.provider_any') }, ...groups]
    : groups;
}

export function ProviderSelect(props: {
  /** Optional, matching this component's own history (a bare `<ProviderSelect
   *  value="" onChange={...}/>` with no enclosing `Field` is still valid,
   *  `ProviderSelect.test.tsx`'s own shape) — an absent id falls back to one
   *  this component mints itself, so `Select` always gets a real `a11y.id`. */
  readonly id?: string;
  readonly 'aria-describedby'?: string | undefined;
  readonly 'aria-invalid'?: true | undefined;
  /** The caller's own `Field` label text — `Select`'s own phone-sheet title,
   *  since each of this component's 4 call sites uses a DIFFERENT label
   *  ("Provider", "Limit to one provider", `donations.key_provider`). */
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  /**
   * Offer the blank "any provider" option. TRUE for a route PIN, where the
   * provider is genuinely optional and blank means "whatever the pool resolves".
   * FALSE for CAPTURE, where blank names no endpoint to dial — so the option is
   * not rendered at all rather than validated against after the fact.
   */
  readonly allowAny?: boolean;
  /** Locked (no `write:billing`, or a caller-held busy state). A `<fieldset
   * disabled>` ancestor already covers real browsers; this is the explicit
   * seam a test environment without that cascade (or a lone mount) can read. */
  readonly disabled?: boolean;
}): JSX.Element {
  const fallbackId = createUniqueId();
  const a11y: FieldA11y = {
    get id() {
      return props.id ?? fallbackId;
    },
    get 'aria-describedby'() {
      return props['aria-describedby'];
    },
    get 'aria-invalid'() {
      return props['aria-invalid'];
    },
  };
  return (
    <Select
      a11y={a11y}
      label={props.label}
      value={props.value}
      options={providerOptions(props.allowAny !== false)}
      disabled={props.disabled ?? false}
      onChange={props.onChange}
      block
    />
  );
}
